import React, { useState, useRef, useEffect } from "react";
import DatePickerComponent from "../datepicker/DatePicker";
import Modal from "../modal/Modal";
import PrimaryButton from "../buttons/PrimaryButton";
import "../../styles/recordedData/recordedData.css";
import SectorsTable from "../sectorsTable/SectorsTable";
import { Icon } from "@iconify/react/dist/iconify.js";
import AdminSectorsTable from "../sectorsTable/AdminSectorsTable";
import SupervisorSectorsTable from "../sectorsTable/SupervisorSectorsTable";
import CoordinatorApproveDataTable from "../sectorsTable/CoordinatorApproveDataTable";
import "../../styles/cards/card.css";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import axios from "axios";
import Cookies from "js-cookie";
import CoordinatorIndexedRecords from "../sectorsTable/CoordinatorIndexedRecords";
import confirmIcon from "../../assets/img/iconamoon--shield-yes-fill.svg";

const SearchBox = ({ placeholder, value, onChange }) => (
  <div className="search-container">
    <div className="search">
      <div className="search-icon">
        <Icon icon="proicons:search" color="#078ECE" width="16" height="16" />
      </div>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
    </div>
  </div>
);

const RecordedDataComponent = ({
  role,
  title,
  placeholder,
  isClerk,
  showSearch = true,
  adminPending,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNewUserConfirmationModal, setShowNewUserConfirmationModal] =
    useState(false);
  const [doneAt, setDoneAt] = useState(new Date());
  const modalRef = useRef(null);
  const [startDate, setStartDate] = useState(new Date("2024-01-01"));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddNewUserModal, setShowAddNewUserModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [districtSearchQuery, setDistrictSearchQuery] = useState("");
  const [sectorSearchQuery, setSectorSearchQuery] = useState("");
  const [operatorSearchQuery, setOperatorSearchQuery] = useState("");
  const [bookTypeFilter, setBookTypeFilter] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const [activeTab, setActiveTab] = useState(
    role === "coordinator" || role === "scanner-operator"
      ? "scanned-records"
      : role === "coordinator-index"
      ? "indexed-records"
      : isClerk
      ? "indexed-records"
      : "scanned-records"
  );
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [scannerOperators, setScannerOperators] = useState([]);
  const [selectedScannerOperator, setSelectedScannerOperator] = useState("");
  const [showAssignConfirmationModal, setShowAssignConfirmationModal] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const roleAssigned = Cookies.get("role");

  useEffect(() => {
    if (showAssignModal) {
      const fetchSites = async () => {
        try {
          const token = Cookies.get("token");
          const response = await axios.get(`${baseUrl}/api/sites`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const sitesData = response.data;
          setSites(sitesData);

          const uniqueProvinces = [...new Set(sitesData.map((site) => site.province))].sort();
          const uniqueDistricts = [...new Set(sitesData.map((site) => site.district))].sort();
          const uniqueSectors = [
            ...new Set(sitesData.map((site) => site.sector).filter((sector) => sector !== null)),
          ].sort();

          setProvinces(uniqueProvinces);
          setDistricts(uniqueDistricts);
          setSectors(uniqueSectors);
        } catch (error) {
          setError("Failed to load location data");
        }
      };

      const fetchScannerOperators = async () => {
        try {
          const token = Cookies.get("token");
          const response = await axios.get(`${baseUrl}/api/users/scanner-operators`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setScannerOperators(response.data);
        } catch (error) {
          setError("Failed to load scanner operators");
        }
      };

      fetchSites();
      fetchScannerOperators();
    }
  }, [showAssignModal, baseUrl]);

  const handleSelectedtDateChange = (date) => {
    setSelectedDate(date);
  };
  const handleStartDateChange = (date) => {
    setStartDate(date);
  };
  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleDoneAtChange = (date) => {
    setDoneAt(date);
  };
  const handleButtonClick = () => {
    setShowModal(true);
  };

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  const handlePasswordFocus = () => {
    if (!password) {
      setPassword(generateRandomPassword());
    }
  };

  const handleSubmitClick = () => {
    setShowModal(false);
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleCloseNewUserConfirmation = () => {
    setShowNewUserConfirmationModal(false);
    setRefreshTable((prev) => !prev);
  };

  useEffect(() => {
    let timer;
    if (showNewUserConfirmationModal) {
      timer = setTimeout(() => {
        handleCloseNewUserConfirmation();
      }, 5000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [showNewUserConfirmationModal]);

  const handleAddNewUserModal = () => {
    setShowAddNewUserModal(true);
  };

  const handleAssignScannerOperatorModal = () => {
    setShowAssignModal(true);
    setSelectedProvince("");
    setSelectedDistrict("");
    setSelectedSector("");
    setSelectedScannerOperator("");
    setError("");
  }

  const handleProvinceChange = (e) => {
    setSelectedProvince(e.target.value);
    setSelectedDistrict("");
    setSelectedSector("");
    setSelectedScannerOperator("");
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setSelectedSector("");
    setSelectedScannerOperator("");
  };

  const handleSectorChange = (e) => {
    setSelectedSector(e.target.value);
    setSelectedScannerOperator("");
  };

  // Handle scanner operator selection
  const handleScannerOperatorChange = (e) => {
    setSelectedScannerOperator(e.target.value);
  };

  // Handle assignment submission
  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProvince || !selectedDistrict || !selectedScannerOperator) {
      setError("Please select province, district, and scanner operator");
      return;
    }

    // Find siteId based on selected province, district, and sector
    const selectedSite = sites.find(
      (site) =>
        site.province === selectedProvince &&
        site.district === selectedDistrict &&
        (selectedSector ? site.sector === selectedSector : true)
    );

    if (!selectedSite) {
      setError("Selected site not found");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const token = Cookies.get("token");
      const response = await axios.post(
        `${baseUrl}/api/users/scanner-operator/assign`,
        {
          scannerOperatorId: parseInt(selectedScannerOperator),
          siteId: selectedSite.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 201) {
        setShowAssignModal(false);
        setShowAssignConfirmationModal(true);
        // Reset form
        setSelectedProvince("");
        setSelectedDistrict("");
        setSelectedSector("");
        setSelectedScannerOperator("");
      }
    } catch (err) {
      if (err.response) {
        if (err.response.status === 409) {
          setError("Scanner operator already assigned to this site");
        } else if (err.response.status === 400) {
          setError(err.response.data.error || "Invalid input");
        } else {
          setError("Failed to assign scanner operator");
        }
      } else {
        setError("Server is not responding. Please try again later");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showAssignConfirmationModal) {
      const timer = setTimeout(() => {
        setShowAssignConfirmationModal(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showAssignConfirmationModal]);

  const handleCloseAddNewUserModal = () => {
    setShowAddNewUserModal(false);
    setEmail("");
    setPassword("");
    setName("");
    setUserRole("");
    setError("");
  };

  const handleCloseAssignScannerOperatorModal = () => {
    setShowAssignModal(false);
  }

  const generateRandomPassword = (length = 12) => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array)).slice(0, length);
  };
  const validateForm = () => {
    if (!email || !password || !name || !userRole) {
      setError("Fill in all required fields");
      return false;
    }

    const validRoles = [
      "SUPER_ADMIN",
      "PROJECT_COORDINATOR",
      "CLIENT",
      "SCANNER_OPERATOR",
      "DATA_ENTRY_CLERK",
      "SCANNER_ASSISTANT",
      "SUPERVISOR",
    ];
    if (!validRoles.includes(userRole)) {
      setError("Invalid role");
      return false;
    }

    return true;
  };

  const handleCreateNewUser = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = Cookies.get("token");
      const res = await axios.post(
        `${baseUrl}/api/users/register`,
        {
          email,
          password,
          name,
          role: userRole,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.status === 201) {
        setShowNewUserConfirmationModal(true);
        setShowAddNewUserModal(false);
        setEmail("");
        setPassword("");
        setName("");
        setUserRole("");
      } else {
        setError("Error creating user");
      }
    } catch (err) {
      if (err.response) {
        if (err.response.status === 409) {
          setError("User with this email already exists");
        } else {
          setError(err.response.data.error || "Error creating user");
        }
      } else {
        setError("Server is not responding. Please try again later");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilteredData(
      data.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, data]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleOperatorSearchChange = (e) => {
    setOperatorSearchQuery(e.target.value);
  };

  const handleDistrictSearchChange = (e) => {
    setDistrictSearchQuery(e.target.value);
  };

  const handleSectorSearchChange = (e) => {
    setSectorSearchQuery(e.target.value);
  };

  const toggleBookTypeFilter = (type) => {
    setBookTypeFilter((prev) => (prev === type ? null : type));
  };

  const datePickers = (
    <div className="date-picker-container">
      <DatePickerComponent
        onDateChange={handleStartDateChange}
        selectedDate={startDate}
        search={true}
      />
      <DatePickerComponent
        onDateChange={handleEndDateChange}
        selectedDate={endDate}
        isEndDate={true}
        startDate={startDate}
      />
    </div>
  );

  return (
    <div className="recorded-data-content">
      {(isClerk ||
        role === "scanner-operator" ||
        role === "coordinator" ||
        role === "coordinator-index") && (
        <div className="tabs">
          <div
            className={`tab ${
              (role === "scanner-operator" || role === "coordinator") &&
              activeTab === "scanned-records"
                ? "active"
                : (role === "coordinator-index" || isClerk) &&
                  activeTab === "indexed-records"
                ? "active"
                : ""
            }`}
            onClick={() =>
              setActiveTab(
                role === "scanner-operator" || role === "coordinator"
                  ? "scanned-records"
                  : "indexed-records"
              )
            }
          >
            {role === "coordinator-index" || isClerk
              ? "Indexed Records"
              : "Scanned Records"}
          </div>
          <div
            className={`tab ${activeTab === "yellow-forms" ? "active" : ""}`}
            onClick={() => setActiveTab("yellow-forms")}
          >
            {role === "coordinator-index" || isClerk
              ? "Indexed Yellow Forms"
              : "Scanned Yellow Forms"}
          </div>
        </div>
      )}
      <div className="title">{title || "YOUR RECORDED DATA"}</div>

      <div className="search-and-upload">
        <div className="search-and-date">
          {showSearch && role === "coordinator" ? (
            <div className="coordinator-filters">
              <SearchBox
                placeholder="Search district"
                value={districtSearchQuery}
                onChange={handleDistrictSearchChange}
              />
              <SearchBox
                placeholder="Search sector"
                value={sectorSearchQuery}
                onChange={handleSectorSearchChange}
              />
              <SearchBox
                placeholder="Search scanner operator"
                value={operatorSearchQuery}
                onChange={handleOperatorSearchChange}
              />
              {activeTab === "scanned-records" && (
                <div className="category-filter">
                  <span className="category-label">Category:</span>
                  {["ACTS", "REGISTER"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={bookTypeFilter === type ? "active" : ""}
                      onClick={() => toggleBookTypeFilter(type)}
                    >
                      {type === "ACTS" ? "Acts" : "Register"}
                    </button>
                  ))}
                </div>
              )}
              {datePickers}
            </div>
          ) : (
            <>
              {showSearch && (
                <SearchBox
                  placeholder={placeholder || "Search sector or district"}
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              )}
              {datePickers}
            </>
          )}
        </div>
        <div className="upload-buttons">
          {!adminPending && role === "admin" && (
            <PrimaryButton
              text="Add New User"
              icon="si:add-fill"
              onClick={handleAddNewUserModal}
            />
          )}
          {/* {role === "coordinator"&& roleAssigned === "PROJECT_COORDINATOR" && (
            <PrimaryButton
              text="Assign Scanner Operators"
              icon="si:add-fill"
              onClick={handleAssignScannerOperatorModal}
            />
          )} */}
        </div>
      </div>

      <Modal
        show={showConfirmationModal}
        onClose={handleCloseConfirmationModal}
      >
        <div className="content confirmation">
          <div className="icon-container">
            <img src={confirmIcon} alt="confirm icon" width="57" height="57" />
          </div>
          <div className="text">You have successfully submitted!</div>
        </div>
      </Modal>

      <Modal show={showAddNewUserModal} onClose={handleCloseAddNewUserModal}>
        <div className="title">ADD NEW USER</div>
        <div className="content">
          <div className="form-container">
            <form>
              <div className="email-and-password">
                <div className="label">
                  <div>Name</div>
                  <input
                    placeholder="Enter user full name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="label">
                  <div>Email</div>
                  <input
                    placeholder="Enter user email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="label">
                <div>Role</div>
                <select
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                >
                  <option value="" disabled>
                    Select Role
                  </option>
                  <option value="SUPER_ADMIN">Admin</option>
                  <option value="SCANNER_OPERATOR">Scanner Operator</option>
                  <option value="CLIENT">Stakeholder</option>
                  <option value="DATA_ENTRY_CLERK">Data Entry Clerk</option>
                  <option value="PROJECT_COORDINATOR">
                    Project Coordinator
                  </option>
                  <option value="SUPERVISOR">
                    Supervisor
                  </option>
                  <option value="SCANNER_ASSISTANT">
                    Scanner Assistant
                  </option>
                </select>
              </div>

              <div className="label password">
                <div>Password</div>
                <input
                  placeholder="Enter user password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={handlePasswordFocus}
                  required
                />
                <span
                  className="password-toggle"
                  onClick={handlePasswordToggle}
                >
                  <Icon
                    icon={
                      showPassword ? "iconoir:eye-off" : "iconoir:eye-solid"
                    }
                    width="18px"
                    height="18px"
                  />
                </span>
              </div>
            </form>
            {error && <div className="error-message">{error}</div>}
          </div>
          <div className="button-group">
            <PrimaryButton
              text={
                loading ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Submit"
                )
              }
              onClick={handleCreateNewUser}
            />
            <PrimaryButton text="Cancel" onClick={handleCloseAddNewUserModal} />
          </div>
        </div>
      </Modal>

      <Modal show={showAssignModal} onClose={handleCloseAssignScannerOperatorModal}>
        <div className="title">ASSIGN SCANNER OPERATOR</div>
        <div className="content">
          <div className="form-container assigned">
            <form onSubmit={handleAssignSubmit}>
              <div className="label">
                <div>Province</div>
                <select
                  name="province"
                  value={selectedProvince}
                  onChange={handleProvinceChange}
                  required
                >
                  <option value="" disabled>
                    Select Province
                  </option>
                  {provinces.map((province) => (
                    <option key={province} value={province}>
                      {province}
                    </option>
                  ))}
                </select>
              </div>
              <div className="label">
                <div>District</div>
                <select
                  name="district"
                  value={selectedDistrict}
                  onChange={handleDistrictChange}
                  disabled={!selectedProvince}
                  required
                >
                  <option value="" disabled>
                    Select District
                  </option>
                  {districts
                    .filter((district) =>
                      sites.some(
                        (site) =>
                          site.province === selectedProvince &&
                          site.district === district
                      )
                    )
                    .map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                </select>
              </div>
              <div className="label">
                <div>Sector</div>
                <select
                  name="sector"
                  value={selectedSector}
                  onChange={handleSectorChange}
                  disabled={!selectedDistrict}
                >
                  <option value="" disabled>
                    Select Sector
                  </option>
                  {sectors
                    .filter((sector) =>
                      sites.some(
                        (site) =>
                          site.province === selectedProvince &&
                          site.district === selectedDistrict &&
                          site.sector === sector
                      )
                    )
                    .map((sector) => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                </select>
              </div>
              <div className="label">
                <div>Scanner Operator</div>
                <select
                  name="scannerOperator"
                  value={selectedScannerOperator}
                  onChange={handleScannerOperatorChange}
                  required
                >
                  <option value="" disabled>
                    Select Scanner Operator
                  </option>
                  {scannerOperators.map((operator) => (
                    <option key={operator.id} value={operator.id}>
                      {operator.name}
                    </option>
                  ))}
                </select>
              </div>
              {error && <div className="error-message">{error}</div>}
            </form>
          </div>
          <div className="button-group">
            <PrimaryButton
              text={
                loading ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Assign"
                )
              }
              onClick={handleAssignSubmit}
            />
            <PrimaryButton text="Cancel" onClick={handleCloseAssignScannerOperatorModal} />
          </div>
        </div>
      </Modal>

      <Modal
        show={showAssignConfirmationModal}
        onClose={() => setShowAssignConfirmationModal(false)}
      >
        <div className="content confirmation">
          <div className="icon-container">
            <img src={confirmIcon} alt="confirm" width="57" height="57" />
          </div>
          <div className="text">Scanner operator assigned successfully!</div>
        </div>
      </Modal>

      <Modal
        show={showNewUserConfirmationModal}
        onClose={handleCloseNewUserConfirmation}
      >
        <div className="content confirmation">
          <div className="icon-container">
            <img src={confirmIcon} alt="confirm" width="57" height="57" />
          </div>
          <div className="text">
            You have successfully created a new user! A welcome email with login credentials and a password reset link has been
            sent.
          </div>
        </div>
      </Modal>

      <div className="table-container">
        <div className="table-padding-wrapper">
          {role === "admin" ? (
            <AdminSectorsTable
              searchQuery={searchQuery}
              startDate={startDate}
              endDate={endDate}
              refreshTrigger={refreshTable}
            />
          ) : role === "scanner-operator" ? (
            <SupervisorSectorsTable
              activeTab={activeTab}
              searchQuery={searchQuery}
              startDate={startDate}
              endDate={endDate}
            />
          ) : role === "coordinator" ? (
            <CoordinatorApproveDataTable
              activeTab={activeTab}
              districtSearchQuery={districtSearchQuery}
              sectorSearchQuery={sectorSearchQuery}
              operatorSearchQuery={operatorSearchQuery}
              bookTypeFilter={activeTab === "scanned-records" ? bookTypeFilter : null}
              startDate={startDate}
              endDate={endDate}
            />
          ) : role === "coordinator-index" ? (
            <CoordinatorIndexedRecords
              activeTab={activeTab}
              searchQuery={searchQuery}
              startDate={startDate}
              endDate={endDate}
            />
          ) : role === "admin-pending" ? (
            <AdminSectorsTable
              pending={true}
              searchQuery={searchQuery}
              startDate={startDate}
              endDate={endDate}
              refreshTrigger={refreshTable}
            />
          ) : (
            <SectorsTable
              activeTab={activeTab}
              searchQuery={searchQuery}
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordedDataComponent;
